import api from './client';
import { IUCNStatus, CITESStatus } from '../utils/enums';
import { asArray } from '../utils/asArray';

interface Photo {
  id: string;
  url: string;
  isAvatar: boolean;
  speciesId?: string;
}

export interface Species {
  id: string;
  commonName: string;
  domain?: string;
  kingdom?: string;
  phylum?: string;
  class?: string;
  genus: string;
  species: string;
  subspecies?: string;
  family?: string;
  order?: string;
  typeCategory: string;
  originLocation: string;
  size: number;
  weight: number;
  colorDescription: string;
  wildTypePhenotype?: string;
  wildTypeNotes?: string;
  captiveBreedingStatus: string;
  longevity: number;
  recommendedHousing: string;
  diet: string;
  nestBoxSize: string;
  clutchSize: number;
  fledgingAge: number;
  hatchingPeriod: number;
  daysBeforeBanding: number;
  bandSize: string;
  iucnStatus: IUCNStatus;
  citesStatus: CITESStatus;
  mutations?: string[];
  fullTaxonomicName: string;
  photos?: Photo[];
  avatarPhotoId?: string;
  createdAt?: string;
  updatedAt?: string;
  
  // Breeding fields
  breedingSeasonStart?: string;
  breedingSeasonEnd?: string;
  incubationPeriod?: number;
  nestingBehavior?: string;
  parentalCarePeriod?: number;
  sexualMaturityAge?: number;
  breedingPairCompatibility?: string;
  nestingMaterialPreferences?: string;
  breedingDifficultyLevel?: string;
  geneticDiversityConcerns?: string;
  
  // Health & Care fields
  lifespan?: number;
  dietRequirements?: string;
  housingRequirements?: string;
  commonHealthIssues?: string;
  vaccinationRequirements?: string;
  parasiteSusceptibility?: string;
  stressFactors?: string;
  environmentalRequirements?: string;
  socialBehavior?: string;
  handlingGuidelines?: string;
  quarantinePeriod?: number;
  veterinaryCareNeeds?: string;
  
  // Market & Sales fields
  marketValueRange?: string;
  marketDemandLevel?: string;
  legalStatus?: string;
  exportRestrictions?: string;
  marketTrends?: string;
  priceRangeMin?: number;
  priceRangeMax?: number;
  buyerPreferences?: string;
  competitionLevel?: string;
  seasonalDemandPatterns?: string;
  
  // AI Metadata fields
  aiDataSources?: string[];
  aiConfidenceScore?: number;
  aiExtractionNotes?: string;
  manualReviewStatus?: string;
  aiProcessingVersion?: string;
  aiLastUpdated?: string;
  /** @deprecated use aiLastUpdated */
  lastAiUpdate?: string;
}

export interface CreateSpeciesDto {
  commonName: string;
  genus: string;
  species: string;
  subspecies?: string;
  typeCategory: string;
  originLocation: string;
  size: number;
  weight: number;
  colorDescription: string;
  captiveBreedingStatus: string;
  longevity: number;
  recommendedHousing: string;
  diet: string;
  nestBoxSize: string;
  clutchSize: number;
  fledgingAge: number;
  hatchingPeriod: number;
  daysBeforeBanding: number;
  bandSize: string;
  iucnStatus: IUCNStatus;
  citesStatus: CITESStatus;
  mutations?: string[];
  
  // Breeding fields
  breedingSeasonStart?: string;
  breedingSeasonEnd?: string;
  incubationPeriod?: number;
  nestingBehavior?: string;
  parentalCarePeriod?: number;
  sexualMaturityAge?: number;
  breedingPairCompatibility?: string;
  nestingMaterialPreferences?: string;
  breedingDifficultyLevel?: string;
  geneticDiversityConcerns?: string;
  
  // Health & Care fields
  lifespan?: number;
  dietRequirements?: string;
  housingRequirements?: string;
  commonHealthIssues?: string;
  vaccinationRequirements?: string;
  parasiteSusceptibility?: string;
  stressFactors?: string;
  environmentalRequirements?: string;
  socialBehavior?: string;
  handlingGuidelines?: string;
  quarantinePeriod?: number;
  veterinaryCareNeeds?: string;
  
  // Market & Sales fields
  marketValueRange?: string;
  marketDemandLevel?: string;
  legalStatus?: string;
  exportRestrictions?: string;
  marketTrends?: string;
  priceRangeMin?: number;
  priceRangeMax?: number;
  buyerPreferences?: string;
  competitionLevel?: string;
  seasonalDemandPatterns?: string;
  
  // AI Metadata fields
  aiDataSources?: string[];
  aiConfidenceScore?: number;
  aiExtractionNotes?: string;
  manualReviewStatus?: string;
  aiProcessingVersion?: string;
  lastAiUpdate?: string;
}

export const getSpecies = async (): Promise<Species[]> => {
  const response = await api.get<Species[] | { items: Species[] }>('/species');
  return asArray<Species>(response.data);
};

export const updateSpecies = async (id: string, data: Partial<Species>): Promise<Species> => {
  const response = await api.patch<Species>(`/species/${id}`, data);
  return response.data;
};

export interface SpeciesCompletenessRow {
  id: string;
  commonName: string;
  fullTaxonomicName: string;
  totalFields: number;
  filledFields: number;
  missingFields: string[];
  completeness: number;
}

export interface CompletenessSummary {
  totalSpecies: number;
  completeSpecies: number;
  incompleteSpecies: number;
  fieldsPerSpecies: number;
  averageCompleteness: number;
  fieldGaps: { field: string; label: string; missing: number }[];
  species: SpeciesCompletenessRow[];
}

export interface EnrichmentResultRow {
  speciesId: string;
  speciesName: string;
  status: 'updated' | 'skipped' | 'failed';
  fieldsFilled?: string[];
  error?: string;
}

export interface EnrichmentJob {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  trigger: 'manual' | 'scheduled';
  total: number;
  processed: number;
  updated: number;
  skipped: number;
  failed: number;
  fieldsFilled: number;
  currentSpecies?: string;
  startedAt: string;
  finishedAt?: string;
  error?: string;
  results: EnrichmentResultRow[];
}

export interface EnrichmentStatus {
  aiConfigured: boolean;
  currentJob: EnrichmentJob | null;
  lastJob: EnrichmentJob | null;
}

export const speciesApi = {
  getAll: async () => {
    const response = await api.get<Species[] | { items: Species[] }>('species');
    return { ...response, data: asArray<Species>(response.data) };
  },
  getById: (id: string) => api.get<Species>(`species/${id}`),
  create: (data: CreateSpeciesDto) => api.post<Species>('species', data),
  update: (id: string, data: Partial<Species>) => api.patch<Species>(`species/${id}`, data),
  delete: (id: string) => api.delete(`species/${id}`),
  // AI data enrichment
  getCompleteness: () => api.get<CompletenessSummary>('species/enrichment/completeness'),
  getEnrichmentStatus: () => api.get<EnrichmentStatus>('species/enrichment/status'),
  runEnrichment: (body: { limit?: number; force?: boolean } = {}) =>
    api.post<EnrichmentJob>('species/enrichment/run', body),
  enrichOne: (id: string, body: { force?: boolean } = {}) =>
    api.post<EnrichmentResultRow>(`species/${id}/enrich`, body),
};

export async function updateSpeciesAvatar(speciesId: string, photoId: string): Promise<Species> {
  const response = await api.post<Species>(`/species/${speciesId}/avatar/${photoId}`);
  return response.data;
} 